<template>
  <div class="concursos-lista">
    <DataTable
      title="Gestão de Concursos"
      subtitle="Gerencie todos os concursos públicos do sistema"
      title-icon="fas fa-clipboard-list"
      :data="concursos"
      :columns="columns"
      :loading="loading"
      :custom-filters="filters"
      search-placeholder="Buscar por título, prefeitura ou tipo..."
      empty-title="Nenhum concurso encontrado"
      empty-message="Não há concursos cadastrados no sistema."
      empty-icon="fas fa-clipboard-list"
      @filter="aplicarFiltros"
      @view="visualizarConcurso"
      @edit="editarConcurso"
      @delete="excluirConcurso"
      @export="exportarConcursos"
    >
      <template #header-actions>
        <router-link :to="{ name: 'admin-concurso-novo' }" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Novo Concurso
        </router-link>
      </template>

      <template #cell-status="{ value }">
        <span class="status-badge" :class="getStatusClass(value)">
          {{ value }}
        </span>
      </template>

      <template #cell-data_prazo_fim="{ value }">
        <div class="date-info">
          <span>{{ formatarData(value) }}</span>
          <small v-if="getDiasRestantes(value) !== null" class="dias-restantes">
            {{ getDiasRestantes(value) > 0 ? `${getDiasRestantes(value)} dias restantes` : 'Encerrado' }}
          </small>
        </div>
      </template>

      <template #cell-total_inscricoes="{ value }">
        <span class="inscricoes-count">
          <i class="fas fa-users"></i>
          {{ value || 0 }}
        </span>
      </template>

      <template #cell-total_vagas="{ value }">
        <span class="vagas-count">
          <i class="fas fa-briefcase"></i>
          {{ value || 0 }}
        </span>
      </template>

      <template #actions="{ item }">
        <button
          @click="visualizarConcurso(item)"
          class="btn-action btn-view"
          title="Visualizar"
        >
          <i class="fas fa-eye"></i>
        </button>
        <button
          @click="editarConcurso(item)"
          class="btn-action btn-edit"
          title="Editar"
        >
          <i class="fas fa-edit"></i>
        </button>
        <button
          @click="gerenciarVagas(item)"
          class="btn-action btn-vagas"
          title="Gerenciar Vagas"
        >
          <i class="fas fa-briefcase"></i>
        </button>
        <button
          @click="gerenciarPublicacoes(item)"
          class="btn-action btn-publicacoes"
          title="Gerenciar Publicações"
        >
          <i class="fas fa-file-alt"></i>
        </button>
        <button
          @click="excluirConcurso(item)"
          class="btn-action btn-delete"
          title="Excluir"
        >
          <i class="fas fa-trash"></i>
        </button>
      </template>
    </DataTable>

    <!-- Modal de Confirmação de Exclusão -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-exclamation-triangle"></i>
            Confirmar Exclusão
          </h4>
          <button @click="closeDeleteModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja excluir o concurso <strong>{{ concursoParaExcluir?.titulo }}</strong>?</p>
          <p class="text-danger">
            <i class="fas fa-warning"></i>
            Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.
          </p>
        </div>
        <div class="modal-footer">
          <button @click="closeDeleteModal" class="btn btn-secondary">
            Cancelar
          </button>
          <button @click="confirmarExclusao" class="btn btn-danger" :disabled="deleting">
            <i v-if="deleting" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-trash"></i>
            {{ deleting ? 'Excluindo...' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import DataTable from '@/components/admin/DataTable.vue'

export default {
  name: 'ConcursosLista',
  components: {
    DataTable
  },
  data() {
    return {
      loading: false,
      deleting: false,
      concursos: [],
      showDeleteModal: false,
      concursoParaExcluir: null,
      columns: [
        {
          key: 'titulo',
          label: 'Título',
          sortable: true
        },
        {
          key: 'prefeitura',
          label: 'Prefeitura',
          sortable: true
        },
        {
          key: 'tipo',
          label: 'Tipo',
          sortable: true
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          type: 'badge'
        },
        {
          key: 'data_prazo_fim',
          label: 'Data de Encerramento',
          sortable: true,
          type: 'date'
        },
        {
          key: 'total_inscricoes',
          label: 'Inscrições',
          sortable: true
        },
        {
          key: 'total_vagas',
          label: 'Vagas',
          sortable: true
        }
      ],
      filters: [
        {
          key: 'prefeitura',
          label: 'Prefeitura',
          placeholder: 'Todas as prefeituras',
          options: []
        },
        {
          key: 'tipo',
          label: 'Tipo',
          placeholder: 'Todos os tipos',
          options: []
        },
        {
          key: 'status',
          label: 'Status',
          placeholder: 'Todos os status',
          options: [
            { value: 'ABERTAS', label: 'Abertas' },
            { value: 'ENCERRADAS', label: 'Encerradas' },
            { value: 'AGUARDANDO', label: 'Aguardando' }
          ]
        }
      ]
    }
  },
  mounted() {
    this.carregarDados()
  },
  methods: {
    async carregarDados() {
      this.loading = true
      try {
        await Promise.all([
          this.carregarConcursos(),
          this.carregarPrefeituras(),
          this.carregarTipos()
        ])
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
        this.$sweet.showError('Erro ao carregar dados dos concursos')
      } finally {
        this.loading = false
      }
    },
    async carregarConcursos() {
      try {
        // Usar rota admin que retorna dados completos com contagens
        const { data } = await axios.get('/ps/concurso/admin/todos')
        this.concursos = data.map(concurso => ({
          ...concurso,
          status: this.calcularStatusInscricoes(concurso),
          // Garantir que as contagens estejam presentes
          total_inscricoes: concurso.inscricoes_count || concurso.total_inscricoes || 0,
          total_vagas: concurso.total_vagas || concurso.vagas_count || 0
        }))
      } catch (error) {
        console.error('Erro ao carregar concursos:', error)
        throw error
      }
    },
    async carregarPrefeituras() {
      try {
        const { data } = await axios.get('/ps/concurso/prefeituras')
        this.filters[0].options = data.map(prefeitura => ({
          value: prefeitura,
          label: prefeitura
        }))
      } catch (error) {
        console.error('Erro ao carregar prefeituras:', error)
      }
    },
    async carregarTipos() {
      try {
        const { data } = await axios.get('/ps/concurso/tipos')
        this.filters[1].options = data.map(tipo => ({
          value: tipo,
          label: tipo
        }))
      } catch (error) {
        console.error('Erro ao carregar tipos:', error)
      }
    },
    calcularStatusInscricoes(concurso) {
      const hoje = new Date()
      const inicio = new Date(concurso.data_prazo_inicio)
      const fim = new Date(concurso.data_prazo_fim)

      if (hoje < inicio) {
        return 'AGUARDANDO'
      } else if (hoje >= inicio && hoje <= fim) {
        return 'ABERTAS'
      } else {
        return 'ENCERRADAS'
      }
    },
    aplicarFiltros(filtros) {
      // Os filtros são aplicados automaticamente pelo componente DataTable
      console.log('Filtros aplicados:', filtros)
    },
    visualizarConcurso(concurso) {
      this.$router.push({ 
        name: 'admin-concurso-detalhes', 
        params: { id: concurso.id } 
      })
    },
    editarConcurso(concurso) {
      this.$router.push({ 
        name: 'admin-concurso-editar', 
        params: { id: concurso.id } 
      })
    },
    gerenciarVagas(concurso) {
      this.$router.push({ 
        name: 'admin-concurso-detalhes', 
        params: { id: concurso.id },
        query: { tab: 'vagas' }
      })
    },
    gerenciarPublicacoes(concurso) {
      this.$router.push({ 
        name: 'admin-concurso-detalhes', 
        params: { id: concurso.id },
        query: { tab: 'publicacoes' }
      })
    },
    excluirConcurso(concurso) {
      this.concursoParaExcluir = concurso
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      this.showDeleteModal = false
      this.concursoParaExcluir = null
    },
    async confirmarExclusao() {
      if (!this.concursoParaExcluir) return

      this.deleting = true
      try {
        await axios.delete(`/ps/concurso/deletar/${this.concursoParaExcluir.id}`)
        this.$sweet.showSuccess('Concurso excluído com sucesso!')
        this.closeDeleteModal()
        this.carregarConcursos()
      } catch (error) {
        console.error('Erro ao excluir concurso:', error)
        this.$sweet.showError('Erro ao excluir concurso')
      } finally {
        this.deleting = false
      }
    },
    exportarConcursos(dados) {
      // Implementar exportação para Excel/PDF
      console.log('Exportando concursos:', dados)
      this.$sweet.showInfo('Funcionalidade de exportação será implementada em breve')
    },
    getStatusClass(status) {
      const classes = {
        'ABERTAS': 'status-abertas',
        'ENCERRADAS': 'status-encerradas',
        'AGUARDANDO': 'status-aguardando'
      }
      return classes[status] || 'status-default'
    },
    formatarData(data) {
      if (!data) return '-'
      
      // Se a data vem no formato YYYY-MM-DD (sem horário), extrair diretamente
      if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
        const [year, month, day] = data.split('-')
        return `${day}/${month}/${year}`
      }
      
      // Usar horário local (sem conversão UTC)
      const date = new Date(data)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      
      return `${day}/${month}/${year}`
    },
    getDiasRestantes(dataFim) {
      if (!dataFim) return null
      
      const fim = new Date(dataFim)
      const hoje = new Date()
      const diffTime = fim - hoje
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      return diffDays
    }
  }
}
</script>

<style lang="scss" scoped>
.concursos-lista {
  .status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;

    &.status-abertas {
      background: rgba(40, 167, 69, 0.1);
      color: #28a745;
    }

    &.status-encerradas {
      background: rgba(220, 53, 69, 0.1);
      color: #dc3545;
    }

    &.status-aguardando {
      background: rgba(255, 193, 7, 0.1);
      color: #ffc107;
    }

    &.status-default {
      background: rgba(108, 117, 125, 0.1);
      color: #6c757d;
    }
  }

  .date-info {
    .dias-restantes {
      display: block;
      font-size: 11px;
      color: #6c757d;
      margin-top: 2px;
    }
  }

  .inscricoes-count,
  .vagas-count {
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 500;

    i {
      color: #667eea;
      font-size: 12px;
    }
  }

  .btn-action {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    font-size: 14px;

    &.btn-view {
      background: #e3f2fd;
      color: #1976d2;

      &:hover {
        background: #bbdefb;
      }
    }

    &.btn-edit {
      background: #f3e5f5;
      color: #7b1fa2;

      &:hover {
        background: #e1bee7;
      }
    }

    &.btn-vagas {
      background: #e8f5e8;
      color: #2e7d32;

      &:hover {
        background: #c8e6c9;
      }
    }

    &.btn-publicacoes {
      background: #fff3e0;
      color: #f57c00;

      &:hover {
        background: #ffe0b2;
      }
    }

    &.btn-delete {
      background: #ffebee;
      color: #d32f2f;

      &:hover {
        background: #ffcdd2;
      }
    }
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 15px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideInUp 0.3s ease-out;
}

.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .modal-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #ffc107;
    }
  }

  .modal-close {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease;

    &:hover {
      background: #f8f9fa;
    }
  }
}

.modal-body {
  padding: 25px;

  p {
    margin-bottom: 15px;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .text-danger {
    color: #dc3545;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }
}

.modal-footer {
  padding: 20px 25px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &.btn-secondary {
      background: #6c757d;
      color: white;

      &:hover {
        background: #5a6268;
      }
    }

    &.btn-danger {
      background: #dc3545;
      color: white;

      &:hover:not(:disabled) {
        background: #c82333;
      }

      &:disabled {
        background: #6c757d;
        cursor: not-allowed;
      }
    }
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
